import * as Sentry from '@sentry/react';
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { captureException } from '../sentry';
import { addLifecycleBreadcrumb } from '../utils/breadcrumbs';

/**
 * Props for the ErrorBoundary component
 */
interface ErrorBoundaryProps {
  children: ReactNode;
  /**
   * Custom fallback UI to show when an error occurs
   */
  fallback?: ReactNode | ((error: Error, resetError: () => void) => ReactNode);
  /**
   * Called when an error is caught
   */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  /**
   * Feature name for context in error reports
   */
  featureName?: string;
  /**
   * Show a retry button in the default fallback
   */
  showRetry?: boolean;
}

/**
 * State for the ErrorBoundary component
 */
interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Default fallback UI when an error occurs
 */
function DefaultFallback({
  error,
  resetError,
  showRetry = true,
}: {
  error: Error;
  resetError: () => void;
  showRetry?: boolean;
}) {
  return (
    <div
      style={{
        padding: '20px',
        textAlign: 'center',
        backgroundColor: '#fef2f2',
        border: '1px solid #fecaca',
        borderRadius: '8px',
        margin: '20px',
      }}
    >
      <h2 style={{ color: '#dc2626', marginBottom: '10px' }}>Something went wrong</h2>
      <p style={{ color: '#7f1d1d', marginBottom: '15px' }}>
        We've been notified and are working to fix the issue.
      </p>
      {process.env.NODE_ENV === 'development' && (
        <details style={{ textAlign: 'left', marginBottom: '15px' }}>
          <summary style={{ cursor: 'pointer', color: '#991b1b' }}>Error details</summary>
          <pre
            style={{
              backgroundColor: '#fff',
              padding: '10px',
              borderRadius: '4px',
              overflow: 'auto',
              fontSize: '12px',
            }}
          >
            {error.message}
            {'\n\n'}
            {error.stack}
          </pre>
        </details>
      )}
      {showRetry && (
        <button
          onClick={resetError}
          style={{
            backgroundColor: '#dc2626',
            color: 'white',
            padding: '8px 16px',
            borderRadius: '4px',
            border: 'none',
            cursor: 'pointer',
          }}
        >
          Try again
        </button>
      )}
    </div>
  );
}

/**
 * ErrorBoundary - Catches React errors and reports them to Sentry
 *
 * This is a custom error boundary that integrates with the observability system.
 * It captures errors, sends them to Sentry, and provides a fallback UI.
 *
 * Usage:
 * ```tsx
 * import { ErrorBoundary } from '@commander/observability';
 *
 * <ErrorBoundary featureName="Dashboard">
 *   <DashboardContent />
 * </ErrorBoundary>
 * ```
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { onError, featureName } = this.props;

    // Add breadcrumb for the error
    addLifecycleBreadcrumb('error', featureName || 'ErrorBoundary', {
      errorMessage: error.message,
      componentStack: errorInfo.componentStack,
    });

    // Capture the error to Sentry
    captureException(error, {
      extra: {
        componentStack: errorInfo.componentStack,
        featureName,
      },
      tags: featureName ? { feature: featureName } : undefined,
    });

    // Call custom error handler if provided
    if (onError) {
      onError(error, errorInfo);
    }
  }

  resetError = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    const { hasError, error } = this.state;
    const { children, fallback, showRetry = true } = this.props;

    if (hasError && error) {
      // Custom fallback
      if (fallback) {
        if (typeof fallback === 'function') {
          return fallback(error, this.resetError);
        }
        return fallback;
      }

      // Default fallback
      return <DefaultFallback error={error} resetError={this.resetError} showRetry={showRetry} />;
    }

    return children;
  }
}

/**
 * Sentry's ErrorBoundary with additional features
 * Use this for advanced Sentry features like showDialog
 */
export const SentryErrorBoundary = Sentry.ErrorBoundary;

/**
 * HOC to wrap a component with an error boundary
 */
export function withErrorBoundary<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  errorBoundaryProps?: Omit<ErrorBoundaryProps, 'children'>
) {
  const displayName = WrappedComponent.displayName || WrappedComponent.name || 'Component';

  function WithErrorBoundary(props: P) {
    return (
      <ErrorBoundary {...errorBoundaryProps}>
        <WrappedComponent {...props} />
      </ErrorBoundary>
    );
  }

  WithErrorBoundary.displayName = `withErrorBoundary(${displayName})`;

  return WithErrorBoundary;
}
